import { destr } from 'destr'

/// Gateway client: Bearer token from storage, single-flight refresh on 401 via
/// `POST /auth/refresh`, and a hard sign-out when the refresh fails.
///
/// The same contract as the admin panel's client (apps/admin/src/lib/api.ts):
/// many requests can 401 at once when the access token expires, and they must
/// share ONE refresh — the refresh token rotates, so a second parallel refresh
/// would present an already-rotated token. Built as a factory so the unit
/// tests can drive it with a fake fetch and an in-memory storage.

export type TokenPair = Readonly<{ accessToken: string; refreshToken: string }>

export class ApiError extends Error {
  readonly status: number
  /// `data.code` from the service — the only key worth branching on.
  readonly code: string | null
  /// The rest of `data` (e.g. `exceeds` on an escalation, `members` on
  /// role_in_use).
  readonly data: Readonly<Record<string, unknown>>

  constructor(
    status: number,
    message: string,
    code: string | null = null,
    data: Readonly<Record<string, unknown>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.data = data
  }
}

/// A request that never reached a server (offline, CORS, DNS).
export class NetworkError extends Error {
  constructor() {
    super('network')
    this.name = 'NetworkError'
  }
}

export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export type Query = Readonly<Record<string, string | number | boolean | null | undefined>>

export type ApiInit = Readonly<{
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /// A multipart body (a file upload). The browser sets the boundary, so no
  /// Content-Type is sent with it. Mutually exclusive with `body`.
  form?: FormData
  query?: Query
  /// No Authorization header and no refresh dance (sign-in, 2FA).
  skipAuth?: boolean
}>

export const ACCESS_TOKEN_KEY = 'club.accessToken'
export const REFRESH_TOKEN_KEY = 'club.refreshToken'

type ClientDeps = Readonly<{
  baseUrl: string
  fetch: typeof fetch
  storage: KeyValueStorage
  /// Called once the session cannot be saved (refresh refused or absent).
  onSessionEnded: () => void
}>

const readBody = async (res: Response): Promise<unknown> => {
  const raw = await res.text()
  return raw.length === 0 ? null : destr(raw)
}

const toApiError = async (res: Response): Promise<ApiError> => {
  const body = await readBody(res)
  const parsed =
    typeof body === 'object' && body !== null
      ? (body as { message?: unknown; statusMessage?: unknown; data?: unknown })
      : {}
  const data =
    typeof parsed.data === 'object' && parsed.data !== null
      ? (parsed.data as Record<string, unknown>)
      : {}
  const message =
    typeof parsed.message === 'string'
      ? parsed.message
      : typeof parsed.statusMessage === 'string'
        ? parsed.statusMessage
        : `HTTP ${res.status}`
  return new ApiError(res.status, message, typeof data['code'] === 'string' ? data['code'] : null, data)
}

export const createApiClient = (deps: ClientDeps) => {
  const getAccessToken = () => deps.storage.getItem(ACCESS_TOKEN_KEY)
  const getRefreshToken = () => deps.storage.getItem(REFRESH_TOKEN_KEY)

  const setTokens = (pair: TokenPair): void => {
    deps.storage.setItem(ACCESS_TOKEN_KEY, pair.accessToken)
    deps.storage.setItem(REFRESH_TOKEN_KEY, pair.refreshToken)
  }

  const clearTokens = (): void => {
    deps.storage.removeItem(ACCESS_TOKEN_KEY)
    deps.storage.removeItem(REFRESH_TOKEN_KEY)
  }

  const url = (path: string, query?: Query): string => {
    const target = new URL(path, deps.baseUrl)
    Object.entries(query ?? {})
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .forEach(([key, value]) => target.searchParams.set(key, String(value)))
    return target.toString()
  }

  const send = async (path: string, init: ApiInit, token: string | null): Promise<Response> => {
    try {
      const json = init.body !== undefined && !init.form
      return await deps.fetch(url(path, init.query), {
        method: init.method ?? (init.body === undefined && !init.form ? 'GET' : 'POST'),
        headers: {
          ...(json ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(init.form ? { body: init.form } : json ? { body: JSON.stringify(init.body) } : {}),
      })
    } catch {
      throw new NetworkError()
    }
  }

  // ---- single-flight refresh ----

  let refreshInFlight: Promise<string | null> | null = null

  const requestRefresh = async (): Promise<string | null> => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return null
    try {
      const res = await send('/auth/refresh', { method: 'POST', body: { refreshToken } }, null)
      if (!res.ok) return null
      const pair = (await readBody(res)) as TokenPair | null
      if (!pair?.accessToken || !pair.refreshToken) return null
      setTokens(pair)
      return pair.accessToken
    } catch {
      return null
    }
  }

  /// Every caller that hits a 401 while a refresh is running waits for that
  /// same refresh instead of starting its own.
  const refreshAccessToken = (): Promise<string | null> => {
    refreshInFlight ??= requestRefresh().finally(() => {
      refreshInFlight = null
    })
    return refreshInFlight
  }

  const endSession = (): never => {
    clearTokens()
    deps.onSessionEnded()
    throw new ApiError(401, 'Session ended', 'session_ended')
  }

  /// A request and its status — for the few answers whose status says
  /// something the body does not: 200 vs 201 on a POST that may have been
  /// a retry of one already written (an idempotency key's replay).
  const exchange = async <T>(path: string, init: ApiInit = {}): Promise<Readonly<{ status: number; body: T }>> => {
    let res = await send(path, init, init.skipAuth ? null : getAccessToken())

    if (res.status === 401 && !init.skipAuth) {
      const fresh = await refreshAccessToken()
      if (!fresh) return endSession()
      res = await send(path, init, fresh)
      if (res.status === 401) return endSession()
    }

    if (!res.ok) throw await toApiError(res)
    if (res.status === 204) return { status: res.status, body: undefined as T }
    return { status: res.status, body: (await readBody(res)) as T }
  }

  const request = async <T>(path: string, init: ApiInit = {}): Promise<T> => (await exchange<T>(path, init)).body

  return {
    request,
    exchange,
    setTokens,
    clearTokens,
    hasSession: (): boolean => Boolean(getAccessToken() || getRefreshToken()),
    getRefreshToken,
  }
}

export type ApiClient = ReturnType<typeof createApiClient>
